import { ChangeDetectorRef, Component, ElementRef, HostListener, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ChartOptions, ChartType } from 'chart.js';

import { firstValueFrom, Observable, Subscription } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';
import { LabelContext } from 'src/app/shared/framework/labels/models/LabelContext';
import { LabelService } from 'src/app/shared/framework/labels/services/label.service';
import { Legend } from 'src/app/modules/framework/home-pages/manager-hr-dashboard/models/Legend';
import { LegendItem } from 'src/app/modules/framework/home-pages/manager-hr-dashboard/models/LegendItem';

import {
  LearningPathOverview,
  LearningPathManagerView,
  LearnpathActivity,
  LearnpathActivityType,
} from '../../models/interfaces';
import { LearnpathService } from '../../services/learnpath.service';

@Component({
  selector: 'app-learnpath-manager',
  templateUrl: './learnpath-manager.component.html',
  styleUrls: ['./learnpath-manager.component.scss'],
})
export class LearnpathManagerComponent implements OnInit, OnDestroy {
  public learnpaths$: Observable<LearningPathOverview[]>;
  public learnpaths: LearningPathOverview[] = [];
  public learnpathsFiltered: LearningPathOverview[] = [];
  public activities$: Observable<LearnpathActivity[]>;

  public activeLearnpath: LearningPathOverview = undefined;
  public View = LearningPathManagerView;
  public activeView: LearningPathManagerView =
    LearningPathManagerView.Employees;

  public isHR = false;

  private _labelContext: LabelContext;
  private subscriptions: Subscription[] = [];


  @ViewChild('chart') chart: ElementRef<HTMLCanvasElement>;
  @ViewChild('chartTooltip') chartTooltip: ElementRef<HTMLDivElement>;
  @ViewChild('chartContainer') chartContainer: ElementRef<HTMLTableElement>;

  public pieChartOptions: ChartOptions<'pie'> | any;
  public pieChartData: any;
  public pieChartType: ChartType = 'pie';
  public pieColors: any[];
  public pieChartLegend = false;
  public dynHeight = 140;
  public legends: Legend[] = [];
  public activityTypes: LearnpathActivityType[] = [];

  public learningpathSearch: string;
  public showActiveLearnpaths: boolean = true;
  public showInactiveLearnpaths: boolean;
  public showLearnpathsWithParticipants: boolean = true;
  public showLearnpathsWithoutParticipants: boolean;

  constructor(
    private _data: LearnpathService,
    private _route: ActivatedRoute,
    private _labelService: LabelService,
    private ref: ChangeDetectorRef
  ) { }

  async ngOnInit() {
    this._labelContext = await firstValueFrom(this._labelService.getContext('learningpath'));

    this.isHR = (localStorage.getItem("corporatehr_view") === '1' || localStorage.getItem("localhr_view") === '1');

    this._data
      .getActivityTypes()
      .pipe(take(1))
      .subscribe((types: LearnpathActivityType[]) => {
        this.activityTypes = types;
      });

    this.learnpaths$ = this._data.getListData().pipe(
      map((e: LearningPathOverview[]) => {
        for (const lp of e) {
          lp.details = lp.details || '';
        }
        return e.filter((a) => a.active || a.notCompletedStudentCount > 0);
      }),
      tap((learnpaths: LearningPathOverview[]) => {
        let lpId = 0;
        const keys = this._route.snapshot.queryParamMap.keys;
        for (const key of keys) {
          if (key === 'id') {
            lpId = +this._route.snapshot.queryParamMap.get(key);
          }
        }

        if (learnpaths.length) {
          let lpSelected = learnpaths[0];
          if (lpId) {
            lpSelected =
              learnpaths.filter((lp) => lp.learningPathId === lpId)[0] ||
              learnpaths[0];
          }

          this.selectLearnPath(
            null,
            lpSelected,
            LearningPathManagerView.Employees
          );
        }
      })
    );
    this.subscriptions.push(
      this.learnpaths$.subscribe(e => {
        this.learnpaths = e;
        this.iterator();
      })
    );
  }

  @HostListener('window:resize')
  onResize() {
    if (this.chartContainer) {
      this.dynHeight = this.chartContainer.nativeElement.clientHeight;
      this.pieChartOptions = { ...this.pieChartOptions };
    } else {
      setTimeout(() => {
        this.onResize();
      }, 500);
    }
  }

  public iterator() {
    let filtered = [];

    let search = (this.learningpathSearch || '').trim().toLowerCase();

    if (search && search.length > 0) {
      filtered = this.learnpaths.filter(e => (e.name).toLowerCase().indexOf(search) > -1);
    } else {
      filtered = this.learnpaths;
    }

    filtered = filtered.filter(e => e.active && this.showActiveLearnpaths || !e.active && this.showInactiveLearnpaths);

    filtered = filtered.filter(e => e.studentCount > 0 && this.showLearnpathsWithParticipants || e.studentCount == 0 && this.showLearnpathsWithoutParticipants);

    this.learnpathsFiltered = [...filtered];
    this.onResize();
  }

  public selectLearnPath(
    event: Event,
    learnpath: LearningPathOverview,
    view: LearningPathManagerView
  ) {
    if (event) {
      event.stopPropagation();
    }

    this.activeView = view;
    this.activities$ = this._data.getActivities(learnpath.learningPathId).pipe(
      map((e) => {
        for (const lpa of e) {
          lpa.description = lpa.description || '';
          lpa.typeName = ' (' + this.activityTypes.filter(e => e.activityTypeId == lpa.activityTypeId)[0].label + ')';
        }
        return e;
      })
    );
    this.activeLearnpath = learnpath;
    window.scrollTo(0, 0);

    if (learnpath.availableDays > 0) {
      this.buildChart(learnpath);
    }
  }

  private buildChart(learnpath: LearningPathOverview) {
    this.setPiechartOptions();

    this.legends = [];
    this.pieChartData = [];

    let beforeDeadline = this._labelContext.get('beforedeadline').labelText;
    let nearDeadline = this._labelContext.get('neardeadline').labelText;
    let pastDeadline = this._labelContext.get('pastdeadline').labelText;

    this.legends.push(
      new Legend('',
        new LegendItem(
          "#28a745",
          beforeDeadline,
          learnpath.notCompletedStudentCount - learnpath.nearDeadline
        ),
        new LegendItem(
          "#ff0019",
          nearDeadline,
          learnpath.nearDeadline - learnpath.actionsRequired
        ),
        new LegendItem(
          "#dc3545",
          pastDeadline,
          learnpath.actionsRequired
        )
      )
    );

    const colors = ["#28a745", "#ffa500", "#dc3545"]
    this.pieColors = [{ backgroundColor: [...colors] }];

    this.pieChartData.push({
      data: [learnpath.notCompletedStudentCount - learnpath.nearDeadline, learnpath.nearDeadline - learnpath.actionsRequired, learnpath.actionsRequired],
      label: '',
      labels: [beforeDeadline, nearDeadline, pastDeadline],
      backgroundColor: colors,
      color: colors,
      url: '',
      ids: [],
    });

    this.pieChartData = this.pieChartData.slice();
    this.ref.markForCheck();
    setTimeout(() => {
      this.onResize();
    }, 250);
  }

  private setPiechartOptions() {
    this.pieChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      cutout: '60%',
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context) {
              const label = (<any>context.dataset).labels[
                context.dataIndex
              ];
              const value = context.dataset.data[context.dataIndex];
              if (value === 987654321) {
                return label;
              } else {
                return label + ': ' + value;
              }
            },
          },
          enabled: false,
          external: (context) => {
            let tooltip
            if (context.tooltip)
              tooltip = context.tooltip
            if (
              !tooltip ||
              !tooltip.body ||
              !tooltip.body.length ||
              !tooltip.body[0].lines
              || tooltip._active.length <= 0
            ) {
              this.chartTooltip.nativeElement.style.opacity = '0';
              this.chartTooltip.nativeElement.style.left = '-99999px';
              this.chartTooltip.nativeElement.style.top = '-999999px';
              return;
            }

            this.chartTooltip.nativeElement.attributes["data-bs-placement"].value =
              (tooltip.yAlign === 'bottom' ? 'top' : 'bottom');
            const html =
              '<div class="tooltip-inner" style="max-width: none;">' +
              tooltip.body[0].lines[0] +
              '</div>';

            this.chartTooltip.nativeElement.innerHTML = html;
            this.chartTooltip.nativeElement.style.opacity = '1';
            this.chartTooltip.nativeElement.style.left =
              this.chart.nativeElement.offsetLeft + tooltip.x + 'px';
            this.chartTooltip.nativeElement.style.top =
              this.chart.nativeElement.offsetTop + tooltip.y + 'px';
          },
        },
      },
      onHover: (event, chartElement) => {
        event.native.target.style.cursor = chartElement[0] ? 'pointer' : 'default';
      }
    }
  }

  public ngOnDestroy(): void {
    for (const subscr of this.subscriptions) {
      subscr.unsubscribe();
    }
  }
}
